import { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";
import { toastCommonestyle } from "../../utils/Toast";

interface SignatureData {
  name: string;
  extension: string;
  data: string;
}

interface SignaturePadProps {
  onSave?: (signature: SignatureData) => void;
  onClose: () => void;
  updatedSignatures: string | "";
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  onSave,
  onClose,
  updatedSignatures,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [imageURL, setImageURL] = useState<string>("");

  // Set initial white background on mount
  useEffect(() => {
    if (updatedSignatures) {
      setImageURL(updatedSignatures);
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  // Mouse event handlers for drawing
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.strokeStyle = "blue"; // Pen color set to blue
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
  };

  const endDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    setIsDrawing(false);
    ctx.closePath();
  };

  // Clear the canvas and reset background to white
  const handleClear = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }

    setImageURL("");
    setIsDrawing(false);

    // Inform parent that signature is cleared
    if (onSave) {
      onSave({ name: "", extension: "", data: "" });
    }

    // Close the dialog
    onClose();
  };

  const extractSignatureInfo = (dataURL: string) => {
    const match = dataURL.match(/^data:(image\/\w+);base64,(.*)$/);
    if (!match) return null;

    const mimeType = match[1]; // e.g., "image/png"
    const extension = mimeType.split("/")[1]; // png, jpeg, etc.
    const name = `signature_${Date.now()}.${extension}`;

    return {
      name,
      extension,
      data: dataURL, // full base64 string including mime type
    };
  };

  // Save the canvas content as a PNG data URL
  const handleSave = () => {
    const dataURL = imageURL || canvasRef.current?.toDataURL("image/png");
    if (!dataURL) {
      return {
        name: "",
        extension: "",
        data: "",
      };
    } else {
      const extracted = extractSignatureInfo(dataURL);
      if (onSave && extracted) {
        onSave(extracted);
      }
    }
  };

  // Upload an image and show it as a preview (do not draw on canvas)
  const MAX_FILE_SIZE_MB = 10;

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg",];
    if (!allowedTypes.includes(file.type)) {
      toast.error(
        "Only JPG, JPEG and PNG files are allowed.",
        toastCommonestyle as any
      );
      return;
    }

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > MAX_FILE_SIZE_MB) {
      toast.error("File size exceeds 10 MB.", toastCommonestyle as any);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setImageURL(base64String); // include full dataURL (data:image/...)
    };
    reader.readAsDataURL(file);
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Signature Pad</DialogTitle>
      <DialogContent>
        {!imageURL && (
          <canvas
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={endDrawing}
            onMouseLeave={endDrawing}
            onTouchStart={(e) => startDrawing(e.touches[0] as any)}
            onTouchMove={(e) => {
              e.preventDefault();
              draw(e.touches[0] as any);
            }}
            onTouchEnd={endDrawing}
            ref={canvasRef}
            width={260}
            height={200}
            style={{
              border: "1px solid #ccc",
              background: "white",
              cursor: "crosshair",
              margin: "auto",
            }}
          />
        )}
        {imageURL && (
          <img
            src={imageURL}
            alt="Uploaded Signature"
            style={{ maxWidth: "280px", marginTop: 10, height: "200px" }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" component="label">
          Upload Image
          <input type="file" accept="image/*" onChange={handleUpload} hidden />
        </Button>
      </DialogActions>
      <DialogActions>
        <Button variant="outlined" onClick={handleSave} color="primary">
          Save
        </Button>
        <Button variant="outlined" onClick={handleClear} color="secondary">
          Clear
        </Button>
        <Button variant="outlined" onClick={onClose} color="error">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
